<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Collecting this information helps your financial professional to
          fulfill regulatory obligations.
        </p>
      </div>
    </help-drawer>
    <v-form name="PoliticallyExposedPerson" class="form" data-test-id="politically-exposed-person-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="politically-exposed-person-header">
              <span class="headerlight">
                Are you or any of your immediate family members considered a <strong>Politically Exposed Person (PEP)?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving politically exposed person status...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isPePComplete && formHidden && !isSyncing"
          :submitted="isPePComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-column class="justify-center full col-12 col-sm-4" v-if="!formHidden && !isSyncing">
            <v-row class="justify-center">
              <v-col class="step-form-container radio-container mb-4">
                <v-radio-group
                  v-model="isPeP"
                  :error-messages="isPePErrors"
                  hide-details="auto"
                  @change="$v.isPeP.$touch()"
                  data-test-id="ispep-radio-group"
                >
                    <v-radio
                    :ripple="false"
                    class="v-faux-radio mb-4"
                    :label="'Yes'"
                    :value="true"
                    data-test-id="pep-true-radio"
                    ></v-radio>
                    <v-radio
                    :ripple="false"
                    class="v-faux-radio"
                    :label="'No'"
                    :value="false"
                    data-test-id="pep-false-radio"
                    ></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
        </v-column>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="politically-exposed-person-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'PoliticallyExposedPerson',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    isPeP: null,
    MENUS: MENUS,
  }),

  validations() {
    return {
      isPeP: {
        required,
      },
      validationGroup: ['isPeP'],
    };
  },

  computed: {
    // Is PeP complete
    isPePComplete() {
      return this.isPageComplete(MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id);
      return page && page.status === 'IN_PROGRESS';
    },
    /* Dynamic errors */
    isPePErrors() {
      const errors = [];
      if (!this.$v.isPeP.$dirty) return errors;
      !this.$v.isPeP.required &&
        errors.push('A selection is required');
      return errors;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        isPeP: this.isPeP,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isPePComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isPePComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id);
    // Initialize form data for current interview
    this.formHidden = this.isPePComplete;
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If PeP is complete, hide the form
      if (this.isPePComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
